import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import { useAsync } from 'react-async-hook';
import { Container, Draggable } from 'react-smooth-dnd';
import arrayMove from 'array-move';
import Linkify from 'react-linkify';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { tasklistApi } from '../apis/tasklist/tasklistApi';
import { useSystemMessage } from '../hooks/useSystemMessage';
import 'react-datepicker/dist/react-datepicker.css';
import { formatOmittedDate } from '../libs/utils/formatDate';
import { CreateTaskModal } from '../components/pages/Task/CreateTaskModal';
import { UpdateTaskModal } from '../components/pages/Task/UpdateTaskModal';
import { AddListModal } from '../components/pages/TaskList/AddListModal';
import { UpdateListModal } from '../components/pages/TaskList/UpdateListModal';
import { taskApi } from '../apis/task/taskApi';
import { ListList, TaskList } from '../types/types';
import icon426 from '../assets/images/ROOM_TaskBoard_Re4_26.png';
import icon434 from '../assets/images/ROOM_TaskBoard_Re4_34.png';
import icon438 from '../assets/images/ROOM_TaskBoard_Re4_38.png';
import icon439 from '../assets/images/ROOM_TaskBoard_Re4_39.png';
import icon442 from '../assets/images/ROOM_TaskBoard_Re4_42.png';
import icon01 from '../assets/images/01icon_checkbox_glay.png';
import icon02 from '../assets/images/02icon_checkbox.png';
import { getLabelColor } from '../libs/utils/getLabelColor';
import useLoading from '../hooks/useLoading';
import { useTaskCollapsed } from '../hooks/useTaskCollapsed';
import useAppRouter from "../hooks/useAppRouter";
import {useAuth} from "../hooks/useAuth";
import {logError} from "../libs/utils/logError";
import {LazyAndPreloadImage} from "../components/pages/common/image/LazyAndPreloadImage";

interface OnDropProps {
  removedIndex: number | null;
  addedIndex: number | null;
  payload?: { index: number };
  element?: HTMLElement | undefined;
}

interface MovedProps {
  taskId?: number;
  removedIndex?: number;
  removedListId?: number;
  addedIndex?: number;
  addedListId?: number;
}

export const TaskListPage: React.FC = () => {
  const { goto } = useAppRouter();
  const { state: { currentUser } } = useAuth();
  if(currentUser?.accessRoomIds.every( id => id !== currentUser?.roomId)) {
    goto('forbiddenError');
  }
  const { success, danger } = useSystemMessage();
  const [listLists, setListLists] = React.useState<ListList[]>([]);
  const [listList, setListList] = React.useState<ListList>();
  const [targetTaskList, setTargetTaskList] = React.useState<TaskList>();
  const [showUpdateListModal, setShowUpdateListModal] = React.useState<boolean>(
    false
  );
  const [showUpdateTaskModal, setShowUpdateTaskModal] = React.useState<boolean>(
    false
  );
  const [showCreateTaskModal, setShowCreateTaskModal] = React.useState<boolean>(
    false
  );
  const [abortController, setAbortController] = React.useState<
    AbortController | undefined
  >();
  const [movedProps, setMovedProps] = React.useState<MovedProps | undefined>();
  const withLoading = useLoading();
  const location = useLocation();
  const { collapsed } = useTaskCollapsed();

  const { execute: reloadTaskList } = useAsync(async () => {
    const fetch = async () => {
      if (abortController !== undefined) {
        abortController.abort();
        setAbortController(undefined);
      }

      setAbortController(new AbortController());

      withLoading(async () => {
        try {
          const response = await tasklistApi.index(true, {
            abortSignal: abortController?.signal
          });
          setListLists(response.listList);
          setAbortController(undefined);
        } catch(e) {
          await logError(
            location.pathname,
            'タスク一覧の取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
  }, []);

  useEffect(() => {
    // 同一リスト内のタスク移動だとonTaskDrop()は一回しか発火しないが、別リスト移動だとremovedとadded別に二回発火する
    // なので、movedPropsをwatchし、すべてのプロパティに値が入ったときAPIを叩くという流れにした
    const handleMoveSubmit = async () => {
      if (
        movedProps?.taskId === undefined ||
        movedProps?.addedListId === undefined ||
        movedProps?.addedIndex === undefined ||
        movedProps?.removedListId === undefined ||
        movedProps?.removedIndex === undefined
      ) {
        return;
      }

      // タスク位置の変動がない場合は処理を行わない
      if (
        movedProps.removedIndex === movedProps.addedIndex &&
        movedProps.removedListId === movedProps.addedListId
      ) {
        setMovedProps(undefined);
        return;
      }

      try {
        // 移動するタスク
        const movedTask = listLists
          .find((ll) => ll.id === movedProps.removedListId)
          ?.taskList.find((tl) => tl.id === movedProps.taskId);
        if (movedTask === undefined) {
          return;
        }

        // 移動元リスト
        const removedList = listLists.find(
          (ll) => ll.id === movedProps.removedListId
        );
        if (removedList === undefined) {
          return;
        }

        // 移動元リストから移動タスクを消す
        removedList.taskList.splice(movedProps.removedIndex, 1);

        // 移動先リスト
        const addedList = listLists.find(
          (ll) => ll.id === movedProps.addedListId
        );
        if (addedList === undefined) {
          return;
        }

        // 移動先リストに移動タスクを追加
        // リストにタスクが一つも無いとき、taskListがundefinedになるので以下の条件分岐が必要
        if (addedList.taskList !== undefined) {
          addedList.taskList.splice(movedProps.addedIndex, 0, movedTask);
        } else {
          addedList.taskList = [movedTask];
        }

        // ここまでの一連のコードについて、listListsのstateを変更せずとも動作はするが、state変更が無いとDOMが高速移動してしまうので必要
        setListLists((prevState) => [...prevState, removedList, addedList]);

        await taskApi.move(
          {
            taskId: movedProps.taskId,
            listId: movedProps.addedListId,
            listOrder: movedProps.addedIndex + 1,
          },

          {}
        );
        setMovedProps(undefined);
        await success('タスクを移動しました');
        await reloadTaskList();
      } catch(e) {
        await logError(
          location.pathname,
          'タスクの移動に失敗しました。',
          e
        );

        goto('internalServerError');
      }
    };

    handleMoveSubmit();
    // eslint-disable-next-line
  }, [movedProps]);

  const onListUpdateSubmitted = async () => {
    await reloadTaskList();
    setShowUpdateListModal(false);
  };

  const onTaskUpdateSubmitted = async () => {
    await reloadTaskList();
    setShowUpdateTaskModal(false);
  };

  const handleListUpdateClick = (list: ListList) => {
    setListList(list);
    setShowUpdateListModal(true);
  };

  const handleTaskUpdateClick = (task: TaskList, list: ListList) => {
    setShowUpdateTaskModal(true);
    setTargetTaskList(task);
    setListList(list);
  };

  const handleTaskCreateClick = (list: ListList) => {
    setShowCreateTaskModal(true);
    setListList(list);
  };

  // removedIndexは移動元のリストの位置(左から・0から)
  // addedIndexは移動先のリストの位置(左から・0から)
  const onListDrop = async ({ removedIndex, addedIndex }: OnDropProps) => {
    if (removedIndex === null || addedIndex === null) {
      return;
    }

    // リスト位置の変動がない場合は処理を行わない
    if (removedIndex === addedIndex) {
      return;
    }

    try {
      setListLists((prevState) =>
        arrayMove(prevState, removedIndex, addedIndex)
      );
      await tasklistApi.move(
        { listId: listLists[removedIndex].id, listOrder: addedIndex + 1 }, // listOrderは1から始まるのでindex + 1
        {}
      );
      await success('リストを移動しました');
      await reloadTaskList();
    } catch(e) {
      await logError(
        location.pathname,
        'タスク一覧の移動に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const onTaskDrop = async (
    listId: number,
    { removedIndex, addedIndex }: OnDropProps
  ) => {
    if (removedIndex === null && addedIndex === null) {
      return;
    }

    if (removedIndex !== null) {
      const taskId = listLists.find((l) => l.id === listId)?.taskList[
        removedIndex
      ].id;
      if (taskId === undefined) {
        await danger('タスクが存在しません。');
        return;
      }
      setMovedProps((prevState) =>
        prevState === undefined
          ? { taskId, removedListId: listId, removedIndex }
          : { ...prevState, taskId, removedListId: listId, removedIndex }
      );
    }

    if (addedIndex !== null) {
      setMovedProps((prevState) =>
        prevState === undefined
          ? { addedListId: listId, addedIndex }
          : { ...prevState, addedListId: listId, addedIndex }
      );
    }
  };

  const handleChecked = async (task: TaskList) => {
    try {
      await taskApi.update(
        task.id,
        { name: task.name, endFlg: !task.endFlg },
        {}
      );
      await success('タスクを更新しました');
      await reloadTaskList();
    } catch(e) {
      await logError(
        location.pathname,
        'タスクの更新に失敗しました。',
        e
      );

      await danger('タスクを更新できませんでした');
      await reloadTaskList();
    }
  };

  return (
    <>
      {showUpdateListModal && listList && (
        <UpdateListModal
          {...{
            onSubmitted: onListUpdateSubmitted,
            listList,
          }}
        />
      )}
      {showUpdateTaskModal && targetTaskList && listList && (
        <UpdateTaskModal
          {...{
            onSubmitted: onTaskUpdateSubmitted,
            taskListId: targetTaskList.id,
            listName: listList.name,
            roomUniqueNo: targetTaskList.roomUniqueNo,
          }}
        />
      )}
      {showCreateTaskModal && listList && (
        <CreateTaskModal
          {...{
            listId: listList.id,
            onSubmitted: reloadTaskList,
            setShowModal: setShowCreateTaskModal,
          }}
        />
      )}
      <div className="task-container">
        <div className="container mx-auto overflow-x-auto h-full">
          <div className="flex h-full overflow-y-hidden">
            <Container
              onDrop={onListDrop}
              orientation="horizontal"
              lockAxis="x"
            >
              {listLists.map((list, listIndex) => (
                <Draggable key={`${list.id}-${listIndex + 1}`}>
                  <section className="list-items mx-2">
                    <div className="rounded-t-lg bg-primary">
                      <div className="flex items-center h-8">
                        <h1
                          className="text-sm text-white flex-grow pl-4 truncate"
                          data-tip={list.name}
                          data-for="tooltip"
                        >
                          {list.name}
                        </h1>
                        <ReactTooltip
                          place="top"
                          type="dark"
                          effect="solid"
                          id="tooltip"
                          className="w-72 break-all text-xs"
                        />
                        <div className="flex-none ml-2">
                          <div className="w-6 h-6 text-primary rounded-md bg-white flex flex-wrap items-center justify-center">
                            {list.taskList ? list.taskList.length : 0}
                          </div>
                        </div>
                        <div className="flex-none">
                          <button
                            type="button"
                            className="flex items-center justify-center p-3 text-white outline-none focus:outline-none"
                            onClick={() => handleListUpdateClick(list)}
                          >
                            <img src={icon426} alt="icon" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="border-2 border-primary bg-gray-50 h-10 my-2 py-auto text-sm flex flex-wrap items-center justify-center w-full outline-none focus:outline-none"
                      onClick={() => handleTaskCreateClick(list)}
                    >
                      <div className="flex items-center">
                        <img src={icon434} alt="icon" />
                        <h1 className="ml-4 text-sm my-auto text-primary font-semibold">
                          タスク追加
                        </h1>
                      </div>
                    </button>
                    <Container
                      onDrop={(e) => onTaskDrop(list.id, e)}
                      orientation="vertical"
                      groupName="task"
                      dropPlaceholder={{
                        animationDuration: 150,
                        showOnTop: true,
                        className: 'drop-card-preview',
                      }}
                    >
                      {list.taskList &&
                        list.taskList.map((taskList, taskIndex) => (
                          <Draggable
                            key={`${list.id}-${taskList.id}-${taskIndex + 1}`}
                          >
                            {collapsed ? (
                              <div className="relative">
                                <div
                                  role="button"
                                  tabIndex={-1}
                                  onClick={() =>
                                    handleTaskUpdateClick(taskList, list)
                                  }
                                  onKeyDown={(event) => event.preventDefault()}
                                  className={`task-items ${
                                    taskList.endFlg && 'bg-gray-300'
                                  }`}
                                >
                                  <div className="w-60">
                                    <h1 className="text-sm mb-3">
                                      <span className="text-xs font-thin mr-2">
                                        {`#${`00${taskList.roomUniqueNo}`.slice(
                                          -2
                                        )}`}
                                      </span>
                                      <span className="task-name">
                                        <Linkify
                                          componentDecorator={(
                                            decoratedHref,
                                            decoratedText,
                                            key
                                          ) => (
                                            <a
                                              target="_blank"
                                              href={decoratedHref}
                                              key={key}
                                              rel="noreferrer"
                                            >
                                              {decoratedText}
                                            </a>
                                          )}
                                        >
                                          {taskList.name}
                                        </Linkify>
                                      </span>
                                    </h1>
                                  </div>
                                  <div className="flex space-x-1">
                                    {taskList.taskLabelList.map((label) => (
                                      <div
                                        key={`${taskList.id}-${label.id}`}
                                        className={`w-16 text-xs font-semibold text-white rounded-full py-1 px-2 mr-2 ${getLabelColor(
                                          label.color
                                        )}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleChecked(taskList)}
                                  className="outline-none focus:outline-none px-4 pb-3 absolute bottom-0 right-0"
                                >
                                  {taskList.endFlg ? (
                                    <img src={icon02} alt="icon" />
                                  ) : (
                                    <img src={icon01} alt="icon" />
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="relative">
                                <div
                                  role="button"
                                  tabIndex={-1}
                                  onClick={() =>
                                    handleTaskUpdateClick(taskList, list)
                                  }
                                  onKeyDown={(event) => event.preventDefault()}
                                  className={`task-items ${
                                    taskList.endFlg && 'bg-gray-300'
                                  }`}
                                >
                                  <div className="divide-y divide-primary">
                                    <div className="pb-3 w-60">
                                      <h1 className="text-sm mb-3">
                                        <span className="text-xs font-thin mr-2">
                                          {`#${`00${taskList.roomUniqueNo}`.slice(
                                            -2
                                          )}`}
                                        </span>
                                        <span className="task-name">
                                          <Linkify
                                            componentDecorator={(
                                              decoratedHref,
                                              decoratedText,
                                              key
                                            ) => (
                                              <a
                                                target="_blank"
                                                href={decoratedHref}
                                                key={key}
                                                rel="noreferrer"
                                              >
                                                {decoratedText}
                                              </a>
                                            )}
                                          >
                                            {taskList.name}
                                          </Linkify>
                                        </span>
                                      </h1>
                                      <p className="task-content">
                                        <Linkify
                                          componentDecorator={(
                                            decoratedHref,
                                            decoratedText,
                                            key
                                          ) => (
                                            <a
                                              target="_blank"
                                              href={decoratedHref}
                                              key={key}
                                              rel="noreferrer"
                                            >
                                              {decoratedText}
                                            </a>
                                          )}
                                        >
                                          {taskList.content}
                                        </Linkify>
                                      </p>
                                    </div>
                                    <div className="pt-3">
                                      <div className="mb-2 flex justify-between">
                                        <p className="block text-xs font-extralight">
                                          {taskList.startTime &&
                                            taskList.endTime && (
                                              <span>
                                                {`${formatOmittedDate(
                                                  taskList.startTime
                                                )} - ${formatOmittedDate(
                                                  taskList.endTime
                                                )}`}
                                              </span>
                                            )}
                                        </p>
                                        <div className="flex justify-end w-28">
                                          <div className="flex items-center justify-start flex-wrap">
                                            {[
                                              ...Array(
                                                Number(
                                                  taskList.checkListNum
                                                    .split('/')
                                                    .slice(-1)
                                                )
                                              ).keys(),
                                            ].map((n, index) =>
                                              Number(
                                                taskList.checkListNum
                                                  .split('/')
                                                  .slice(0, 1)
                                              ) <= index ? (
                                                <img
                                                  key={n}
                                                  src={icon439}
                                                  alt="icon"
                                                />
                                              ) : (
                                                <img
                                                  key={n}
                                                  src={icon438}
                                                  alt="icon"
                                                />
                                              )
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex space-x-1 mb-2">
                                        {taskList.assignUser !== undefined && (
                                          <img src={icon442} alt="icon" />
                                        )}
                                      </div>
                                      <div className="flex flex-wrap">
                                        {taskList.taskLabelList.map((label) => (
                                          <>
                                            <span
                                              key={`${taskList.id}-${label.id}`}
                                              className={`label-item-6 mb-2 ${getLabelColor(
                                                label.color
                                              )}`}
                                              data-tip={label.name}
                                              data-for={`label-${label.id}`}
                                            >
                                              {label.name}
                                            </span>
                                            <ReactTooltip
                                              place="top"
                                              type="dark"
                                              effect="solid"
                                              id={`label-${label.id}`}
                                              className="max-w-xs break-all text-xs"
                                            />
                                          </>
                                        ))}
                                      </div>
                                      {taskList.fileURL && taskList.mimeType && (
                                        <div className="max-h-40 overflow-hidden mb-8">
                                          {taskList.mimeType.includes(
                                            'image'
                                          ) ? (
                                            <>
                                              <LazyAndPreloadImage
                                                {...{
                                                  path: taskList.fileURL
                                                }}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <FontAwesomeIcon
                                                icon={faFile}
                                                size="2x"
                                                className="text-gray-500"
                                              />
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleChecked(taskList)}
                                  className="outline-none focus:outline-none p-1 mx-3 mb-2 absolute bottom-0 right-0"
                                >
                                  {taskList.endFlg ? (
                                    <img src={icon02} alt="icon" />
                                  ) : (
                                    <img src={icon01} alt="icon" />
                                  )}
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}
                    </Container>
                  </section>
                </Draggable>
              ))}
            </Container>
            <section className="list-items mx-2">
              <AddListModal {...{ onSubmitted: reloadTaskList }} />
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
